import { useState } from 'react';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { format } from 'date-fns';
import { X } from 'lucide-react';
import api from '@/lib/api';
import type { CareLog, CareLogType } from '@/types';
import { QuickLogButton } from './QuickLogButton';
import { useAuth } from '@/hooks/useAuth';

interface CareLogPanelProps {
  date: string; // YYYY-MM-DD
  slotId?: string; // optional — links log to a shift
  compact?: boolean; // hide the timeline when true
}

const LOG_TYPES: { type: CareLogType; emoji: string; label: string; color: string; hasExtra?: 'severity' | 'quantity' | 'notes' }[] = [
  { type: 'urine', emoji: '💧', label: 'Urine', color: '#3b82f6', hasExtra: 'quantity' },
  { type: 'bm', emoji: '💩', label: 'BM', color: '#d97706', hasExtra: 'quantity' },
  { type: 'microseizure', emoji: '⚡', label: 'Seizure', color: '#ef4444', hasExtra: 'severity' },
  { type: 'exercise', emoji: '🏃', label: 'Exercise', color: '#10b981' },
  { type: 'meal', emoji: '🍽️', label: 'Meal', color: '#f97316' },
  { type: 'medication', emoji: '💊', label: 'Meds', color: '#8b5cf6' },
  { type: 'note', emoji: '📝', label: 'Note', color: '#6b7280', hasExtra: 'notes' },
];

const EMOJI_MAP: Record<string, string> = {
  urine: '💧', bm: '💩', microseizure: '⚡', exercise: '🏃',
  meal: '🍽️', medication: '💊', note: '📝', other: '📋',
};

export function CareLogPanel({ date, slotId, compact }: CareLogPanelProps) {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const [extraModal, setExtraModal] = useState<{ type: CareLogType; extra: 'severity' | 'quantity' | 'notes' } | null>(null);
  const [noteText, setNoteText] = useState('');

  // Fetch logs for date
  const { data: logs = [] } = useQuery({
    queryKey: ['care-logs', date],
    queryFn: async () => {
      const res = await api.get(`/care-logs?date=${date}`);
      return (res.data.logs || []) as CareLog[];
    },
    staleTime: 1000 * 10,
    refetchInterval: 15000,
  });

  // Create mutation
  const createLog = useMutation({
    mutationFn: async (data: { log_type: CareLogType; notes?: string; severity?: string; quantity?: string }) => {
      const res = await api.post('/care-logs', {
        ...data,
        slot_id: slotId || undefined,
        logged_at: new Date().toISOString(),
      });
      return res.data.log;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['care-logs', date] });
    },
  });

  // Delete mutation
  const deleteLog = useMutation({
    mutationFn: async (logId: string) => {
      await api.delete(`/care-logs/${logId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['care-logs', date] });
    },
  });

  const handleQuickLog = (logType: typeof LOG_TYPES[number]) => {
    if (logType.hasExtra) {
      setExtraModal({ type: logType.type, extra: logType.hasExtra });
      setNoteText('');
    } else {
      createLog.mutate({ log_type: logType.type });
    }
  };

  const handleSubmitExtra = (value: string) => {
    if (!extraModal) return;
    const data: any = { log_type: extraModal.type };
    if (extraModal.extra === 'severity') data.severity = value;
    else if (extraModal.extra === 'quantity') data.quantity = value;
    else if (extraModal.extra === 'notes') data.notes = value;
    createLog.mutate(data);
    setExtraModal(null);
    setNoteText('');
  };

  const formatLogTime = (isoStr: string) => {
    try {
      const d = new Date(isoStr);
      return format(d, 'h:mm a');
    } catch {
      return '';
    }
  };

  return (
    <div className="space-y-3">
      {/* Quick log buttons */}
      <div className="flex flex-wrap gap-2 justify-center">
        {LOG_TYPES.map((lt) => (
          <QuickLogButton
            key={lt.type}
            emoji={lt.emoji}
            label={lt.label}
            color={lt.color}
            disabled={createLog.isPending}
            onClick={() => handleQuickLog(lt)}
          />
        ))}
      </div>

      {/* Extra input modal */}
      {extraModal && (
        <div className="p-3 bg-slate-700/50 border border-slate-600 rounded-lg space-y-2">
          {extraModal.extra === 'severity' && (
            <>
              <p className="text-sm text-gray-300">Seizure severity:</p>
              <div className="flex gap-2">
                {(['mild', 'moderate', 'severe'] as const).map((s) => (
                  <button
                    key={s}
                    onClick={() => handleSubmitExtra(s)}
                    className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-colors ${
                      s === 'mild' ? 'bg-yellow-500/20 text-yellow-400 hover:bg-yellow-500/30' :
                      s === 'moderate' ? 'bg-orange-500/20 text-orange-400 hover:bg-orange-500/30' :
                      'bg-red-500/20 text-red-400 hover:bg-red-500/30'
                    }`}
                  >
                    {s.charAt(0).toUpperCase() + s.slice(1)}
                  </button>
                ))}
              </div>
            </>
          )}
          {extraModal.extra === 'quantity' && (
            <>
              <p className="text-sm text-gray-300">{extraModal.type === 'urine' ? 'Urine' : 'BM'} amount:</p>
              <div className="flex gap-2">
                {(['small', 'normal', 'large'] as const).map((q) => (
                  <button
                    key={q}
                    onClick={() => handleSubmitExtra(q)}
                    className="flex-1 py-2 px-3 rounded-lg text-sm font-medium bg-slate-600 text-gray-300 hover:bg-slate-500 transition-colors"
                  >
                    {q.charAt(0).toUpperCase() + q.slice(1)}
                  </button>
                ))}
              </div>
            </>
          )}
          {extraModal.extra === 'notes' && (
            <>
              <p className="text-sm text-gray-300">Add a note:</p>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={noteText}
                  onChange={(e) => setNoteText(e.target.value)}
                  placeholder="Type a note..."
                  autoFocus
                  onKeyDown={(e) => { if (e.key === 'Enter' && noteText.trim()) handleSubmitExtra(noteText.trim()); }}
                  className="flex-1 px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <button
                  onClick={() => noteText.trim() && handleSubmitExtra(noteText.trim())}
                  disabled={!noteText.trim()}
                  className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white text-sm rounded-lg transition-colors disabled:opacity-50"
                >
                  Save
                </button>
              </div>
            </>
          )}
          <button
            onClick={() => { setExtraModal(null); setNoteText(''); }}
            className="text-xs text-gray-400 hover:text-gray-300"
          >
            Cancel
          </button>
        </div>
      )}

      {/* Timeline of today's logs */}
      {!compact && logs.length > 0 && (
        <div className="space-y-1.5 max-h-[300px] overflow-y-auto">
          {logs.map((log) => (
            <div
              key={log.id}
              className="flex items-center gap-2 p-2 bg-slate-800/50 border border-slate-700/50 rounded-lg group"
            >
              <span className="text-lg flex-shrink-0">{EMOJI_MAP[log.log_type] || '📋'}</span>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5">
                  <span className="text-xs text-gray-400">{formatLogTime(log.logged_at)}</span>
                  <span className="text-sm text-gray-200 capitalize">{log.log_type === 'bm' ? 'BM' : log.log_type}</span>
                  {log.severity && (
                    <span className={`text-[10px] px-1.5 py-0.5 rounded ${
                      log.severity === 'mild' ? 'bg-yellow-500/20 text-yellow-400' :
                      log.severity === 'moderate' ? 'bg-orange-500/20 text-orange-400' :
                      'bg-red-500/20 text-red-400'
                    }`}>
                      {log.severity}
                    </span>
                  )}
                  {log.quantity && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-600 text-gray-300">
                      {log.quantity}
                    </span>
                  )}
                </div>
                {log.notes && (
                  <p className="text-xs text-gray-400 truncate">{log.notes}</p>
                )}
                {log.caretaker && log.caretaker.id !== user?.id && (
                  <p className="text-[10px] text-gray-500">
                    {log.caretaker.emoji} {log.caretaker.first_name}
                  </p>
                )}
              </div>
              {(log.caretaker_id === user?.id || user?.role === 'parent') && (
                <button
                  onClick={() => deleteLog.mutate(log.id)}
                  className="p-1 text-gray-600 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-all flex-shrink-0"
                >
                  <X size={14} />
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {!compact && logs.length === 0 && (
        <p className="text-center text-xs text-gray-500 py-2">No care events logged yet today</p>
      )}
    </div>
  );
}
